import React, { useEffect, useState } from 'react';
import agent from '../../services/Api/agent';
import { RainfallResponseDto, RainfallDto } from '../../models/Weather';

interface RainfallDisplayContainerProps {
    stationId?: string; // Specific station ID to display (e.g., "RF014")
    lang?: string;
    autoRefresh?: boolean;
    refreshInterval?: number; // in milliseconds, default 15 minutes
    maxStations?: number; // Maximum number of stations to display, default 10
    showAllStations?: boolean; // Show all stations or just top stations with rainfall
}

export default function RainfallDisplayContainer({
    stationId,
    lang = 'en',
    autoRefresh = true,
    refreshInterval = 300000, // 15 minutes default (API updates every 15 minutes)
    maxStations = 10,
    showAllStations = false
}: RainfallDisplayContainerProps) {
    const [rainfallData, setRainfallData] = useState<RainfallResponseDto | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
    const [displayStations, setDisplayStations] = useState<RainfallDto[]>([]);

    const fetchRainfallData = async () => {
        try {
            setLoading(true);
            setError(null);

            // If stationId is provided, fetch only that station
            const data = stationId
                ? await agent.Weather.getHourlyRainfallByStationId(stationId, lang)
                : await agent.Weather.getHourlyRainfall(lang);

            setRainfallData(data);
            setLastUpdate(new Date());

            // Filter and sort stations
            let stations = data.hourlyRainfall;

            // If stationId is provided, we already have only that station
            if (!stationId) {
                if (!showAllStations) {
                    // Filter out stations with no rainfall (value === "0" or "M")
                    stations = stations.filter(station => {
                        const value = station.value.trim();
                        return value !== "0" && value !== "M" && value !== "";
                    });
                }

                // Sort by rainfall value (descending)
                stations.sort((a, b) => {
                    const aValue = parseFloat(a.value) || 0;
                    const bValue = parseFloat(b.value) || 0;
                    return bValue - aValue;
                });

                // Limit to maxStations
                if (stations.length > maxStations) {
                    stations = stations.slice(0, maxStations);
                }
            }

            setDisplayStations(stations);
        } catch (err: any) {
            console.error('Error fetching rainfall data:', err);
            setError(err.message || 'Failed to fetch rainfall data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Initial fetch
        fetchRainfallData();

        // Set up auto-refresh if enabled
        let intervalId: NodeJS.Timeout | null = null;
        if (autoRefresh) {
            intervalId = setInterval(() => {
                fetchRainfallData();
            }, refreshInterval);
        }

        // Cleanup interval on unmount
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [stationId, lang, autoRefresh, refreshInterval, maxStations, showAllStations]);

    const formatObsTime = (obsTimeStr: string): string => {
        try {
            // Parse ISO 8601 format: "2025-12-22T15:00:00+08:00"
            const date = new Date(obsTimeStr);
            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
        } catch {
            return obsTimeStr;
        }
    };

    const getRainfallValue = (value: string): number => {
        const numValue = parseFloat(value);
        return isNaN(numValue) ? 0 : numValue;
    };

    const getRainfallColor = (value: string): string => {
        const numValue = getRainfallValue(value);
        if (numValue === 0) return 'text-gray-500';
        if (numValue < 5) return 'text-blue-600';
        if (numValue < 10) return 'text-yellow-600';
        if (numValue < 20) return 'text-orange-600';
        return 'text-red-600';
    };

    const getRainfallBgColor = (value: string): string => {
        const numValue = getRainfallValue(value);
        if (numValue === 0) return 'bg-gray-50';
        if (numValue < 5) return 'bg-blue-50';
        if (numValue < 10) return 'bg-yellow-50';
        if (numValue < 20) return 'bg-orange-50';
        return 'bg-red-50';
    };

    if (loading && !rainfallData) {
        return (
            <div className="bg-white rounded-xl border-2 border-gray-200 shadow-md p-6">
                <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-600">Loading...</span>
                </div>
            </div>
        );
    }

    if (error && !rainfallData) {
        return (
            <div className="bg-white rounded-xl border-2 border-red-200 shadow-md p-6">
                <div className="flex items-center justify-center h-32">
                    <div className="text-red-600 text-center">
                        <p className="font-semibold">Failed to Load</p>
                        <p className="text-sm mt-1">{error}</p>
                        <button
                            onClick={fetchRainfallData}
                            className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!rainfallData) {
        return null;
    }

    return (
        <div className="bg-white rounded-xl border-2 border-gray-200 shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-white text-xl font-bold">
                            {'Hong Kong International Airport -  Hourly Rainfall'}
                        </h3>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                        {autoRefresh && (
                            <span className="text-xs text-white">
                                Auto Refresh: Every {Math.floor(refreshInterval / 60000)} minutes
                            </span>
                        )}
                        <button
                            onClick={fetchRainfallData}
                            disabled={loading}
                            className="px-3 py-2 bg-white hover:bg-gray-100 text-blue-600 rounded-lg border-2 border-white hover:border-blue-300 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg flex items-center justify-center"
                            title="Refresh Data"
                        >
                            <svg
                                className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="p-6">
                {/* Observation Time */}
                <div>
                    <div className="flex">
                        <div>
                            <p className="text-gray-500 text-sm mb-1">Record Time</p>
                            <p className="text-gray-900 text-lg font-semibold">
                                {rainfallData.obsTime && formatObsTime(rainfallData.obsTime)}
                            </p>
                            {lastUpdate && (
                                <p className="text-gray-400 text-xs mt-1">
                                    Last Updated: {lastUpdate.toLocaleTimeString('en-US')}
                                </p>
                            )}
                        </div>


                        <div className="ml-auto">
                            <div className="bg-blue-50 rounded-lg p-6 border-2 border-blue-300 shadow-md">
                                <div className="flex items-center justify-between">
                                    <span className={`text-5xl font-bold ${getRainfallColor(displayStations[0].value)}`}>
                                        <p className="text-blue-700 text-sm font-medium mb-2">Hourly Rainfall</p>
                                        <p className="text-blue-900 text-5xl font-bold">
                                            {getRainfallValue(displayStations[0].value).toFixed(1)}
                                            <span className="text-lg text-blue-700 ml-1">{displayStations[0].unit}</span>
                                        </p>
                                    </span>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>


            </div>
        </div>
    );
}

